/**
 * SupervisorDashboard — pending-inspection queue with approve / reject.
 *
 * Renders the cleaning_tasks the supervisor's organization has submitted
 * (status = 'completed') in oldest-first order. Each row exposes:
 *
 *   - a link to TaskDetailPage so the supervisor can review checklist
 *     items, photos, and the cleaning-assistant coaching note before
 *     deciding;
 *   - an inline Approve button (one-click; the underlying SQL RPC
 *     enforces atomic state + role checks);
 *   - an inline Reject button that opens a reason input — empty reasons
 *     are blocked client-side AND server-side. Rejection routes the
 *     room back to maintenance_required so the cleaner can re-engage.
 *
 * Both actions are wired to the existing `complete-task` Edge Function
 * via useApproveTask / useRejectTask. This page does not touch the SQL
 * transition spine.
 */

import { useEffect, useState, type ReactNode } from 'react'
import { Link, useSearchParams }     from 'react-router'
import { useTranslation }            from 'react-i18next'
import { taskDetailPath, PATHS }     from '@/router/paths'
import { useRole }                   from '@/hooks/useAuth'
import {
  usePendingInspections,
  type PendingInspectionRow,
}                                    from '@/hooks/usePendingInspections'
import { useApproveTask }            from '@/hooks/useApproveTask'
import { useRejectTask }             from '@/hooks/useRejectTask'
import { useOrgCampuses }            from '@/hooks/useOrgPickers'
import { useMyTasks, type MyTaskRow } from '@/hooks/useMyTasks'
import { AppError }                  from '@/lib/errors/AppError'
import { useCurrentCampusStore }     from '@/store/currentCampusStore'

export function SupervisorDashboard(): ReactNode {
  const { t } = useTranslation()
  const role  = useRole()
  const canSeeCorrections = role === 'super_admin' || role === 'contractor_admin'

  // Campus filter — synced to ?campus=<id> so a refresh / bookmark
  // preserves the scope. Empty/absent = all campuses.
  const [searchParams, setSearchParams] = useSearchParams()
  const campusParam = searchParams.get('campus')
  const campusId    = campusParam && campusParam.length > 0 ? campusParam : null

  const campuses = useOrgCampuses()
  const { data, isLoading, error } = usePendingInspections(campusId)
  const myTasks                    = useMyTasks()

  // Publish active campus to the sidebar chip (P2 #11). Empty filter
  // = all campuses → clear the chip. Effect runs on every campusId
  // change *and* whenever campuses load (the name lookup depends on
  // the list being populated).
  const setCurrentCampus = useCurrentCampusStore((s) => s.setCurrentCampus)
  useEffect(() => {
    if (!campusId) {
      setCurrentCampus(null, null)
      return
    }
    const match = (campuses.data ?? []).find((c) => c.id === campusId)
    setCurrentCampus(campusId, match?.name ?? null)
  }, [campusId, campuses.data, setCurrentCampus])

  function onCampusChange(next: string): void {
    const updated = new URLSearchParams(searchParams)
    if (next) updated.set('campus', next)
    else      updated.delete('campus')
    setSearchParams(updated, { replace: true })
  }

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <header style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.4rem', fontFamily: 'var(--brand-font-family-display)' }}>
            {t('supervisor.pendingInspections', 'Pending inspections')}
          </h1>
          <p style={{ margin: '0.25rem 0 0', color: 'var(--brand-neutral-500)' }}>
            {t('supervisor.reviewSubmitted', 'Tasks the cleaners have submitted, oldest first.')}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <Link
            to={PATHS.taskCreate}
            data-testid="supervisor-new-task-link"
            style={{
              fontSize:       '0.85rem',
              color:          'var(--brand-white)',
              background:     'var(--brand-primary)',
              textDecoration: 'none',
              padding:        '0.4rem 0.7rem',
              borderRadius:   '0.3rem',
              fontWeight:     600,
            }}
          >
            {t('supervisor.newTaskLink', '+ New task')}
          </Link>
          {canSeeCorrections && (
            <Link
              to={PATHS.corrections}
              style={{
                fontSize:       '0.85rem',
                color:          'var(--brand-neutral-700)',
                textDecoration: 'none',
                padding:        '0.4rem 0.6rem',
                border:         '1px solid var(--brand-neutral-300, #d1d5db)',
                borderRadius:   '0.3rem',
              }}
            >
              {t('supervisor.governanceLink', 'MCP corrections →')}
            </Link>
          )}
        </div>
      </header>

      {/*
        Campus filter — visible only when the tenant has 2+ campuses.
        Single-campus tenants (or tenants on the office / property-mgmt
        property types) don't need a picker.
      */}
      {(campuses.data ?? []).length > 1 && (
        <div
          data-testid="supervisor-campus-filter"
          style={{
            display:    'flex',
            alignItems: 'center',
            gap:        '0.5rem',
            fontSize:   '0.85rem',
            color:      'var(--brand-neutral-700)',
          }}
        >
          <label htmlFor="campus-filter">{t('supervisor.campusFilterLabel', 'Campus:')}</label>
          <select
            id="campus-filter"
            data-testid="campus-filter-select"
            value={campusId ?? ''}
            onChange={(e) => onCampusChange(e.target.value)}
            style={{
              padding:      '0.35rem 0.5rem',
              borderRadius: 'var(--brand-radius-sm)',
              border:       '1px solid var(--brand-neutral-300)',
              background:   'var(--brand-white)',
              fontSize:     '0.85rem',
            }}
          >
            <option value="">{t('supervisor.campusAll', 'All campuses')}</option>
            {(campuses.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
      )}

      {myTasks.data && myTasks.data.length > 0 && (
        <MyCleaningJobsStrip rows={myTasks.data} />
      )}

      {isLoading && <Hint>{t('supervisor.loading', 'Loading queue…')}</Hint>}
      {error    && (
        <ErrorPanel
          message={
            error instanceof Error
              ? error.message
              : t('supervisor.failedToLoad', 'Failed to load the inspection queue.')
          }
        />
      )}

      {!isLoading && !error && (
        (data && data.length > 0)
          ? <InspectionList rows={data} />
          : <Hint>{t('supervisor.empty', 'No tasks waiting for inspection right now.')}</Hint>
      )}
    </section>
  )
}

// ─────────────────────────────────────────────────────────────────────
// "My cleaning jobs" — supervisor-as-assignee strip. Renders only when
// the supervisor has at least one task with assigned_to_user_id matching
// their own user id. Common case is zero rows (supervisors usually just
// inspect), so the section disappears entirely instead of showing an
// "empty" hint. Added 2026-05-18 alongside migration 20260518000010.
// ─────────────────────────────────────────────────────────────────────

function MyCleaningJobsStrip({ rows }: { rows: MyTaskRow[] }): ReactNode {
  const { t } = useTranslation()
  return (
    <section
      data-testid="supervisor-my-jobs"
      aria-label={t('supervisor.myJobsLabel', 'Cleaning jobs assigned to me')}
      style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
    >
      <header>
        <h2 style={{ margin: 0, fontSize: '1.05rem', fontFamily: 'var(--brand-font-family-display)' }}>
          {t('supervisor.myJobs', 'My cleaning jobs')}
        </h2>
        <p style={{ margin: '0.15rem 0 0', color: 'var(--brand-neutral-500)', fontSize: '0.85rem' }}>
          {t('supervisor.myJobsHint', 'Jobs assigned to you. Tap to open and start cleaning.')}
        </p>
      </header>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {rows.map((row) => (
          <li key={row.id}>
            <Link
              to={taskDetailPath(row.id)}
              data-testid={`supervisor-my-job-row-${row.id}`}
              style={{
                display:        'block',
                padding:        '0.75rem 1rem',
                background:     'var(--brand-white)',
                border:         '1px solid var(--brand-neutral-300)',
                borderRadius:   'var(--brand-radius-md)',
                textDecoration: 'none',
                color:          'var(--brand-neutral-900)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <strong>{t('dashboard.room', { number: row.room?.room_number ?? '—' })}</strong>
                <span
                  style={{
                    fontSize:      '0.75rem',
                    padding:       '0.1rem 0.5rem',
                    borderRadius:  'var(--brand-radius-full)',
                    background:    'var(--brand-primary-subtle)',
                    color:         'var(--brand-primary)',
                    textTransform: 'capitalize',
                  }}
                >
                  {row.status.replace(/_/g, ' ')}
                </span>
              </div>
              <div style={{ marginTop: '0.25rem', color: 'var(--brand-neutral-500)', fontSize: '0.85rem' }}>
                {t('dashboard.clean', { type: row.cycle_type })}
                {row.started_at && <> · {t('dashboard.started', { date: new Date(row.started_at).toLocaleString() })}</>}
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  )
}

// ─────────────────────────────────────────────────────────────────────

function InspectionList({ rows }: { rows: PendingInspectionRow[] }): ReactNode {
  return (
    <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      {rows.map((row) => <InspectionRow key={row.id} row={row} />)}
    </ul>
  )
}

function InspectionRow({ row }: { row: PendingInspectionRow }): ReactNode {
  const { t } = useTranslation()
  const approve = useApproveTask()
  const reject  = useRejectTask()

  const [showRejectForm, setShowRejectForm] = useState(false)
  const [rejectReason,   setRejectReason]   = useState('')
  const [actionError,    setActionError]    = useState<AppError | null>(null)

  const isBusy   = approve.isPending || reject.isPending
  const cleaner  = row.cleaner?.full_name ?? t('supervisor.unknownCleaner', 'Unknown cleaner')
  const completedAt = row.completed_at
    ? new Date(row.completed_at).toLocaleString()
    : t('supervisor.unknownTime', 'unknown time')

  const onApprove = async (): Promise<void> => {
    setActionError(null)
    try {
      await approve.mutateAsync({ taskId: row.id })
    } catch (e) {
      setActionError(e instanceof AppError ? e : null)
    }
  }

  const onReject = async (): Promise<void> => {
    setActionError(null)
    try {
      await reject.mutateAsync({ taskId: row.id, rejectionReason: rejectReason })
      setShowRejectForm(false)
      setRejectReason('')
    } catch (e) {
      setActionError(e instanceof AppError ? e : null)
    }
  }

  return (
    <li
      style={{
        background:   'var(--brand-white)',
        border:       '1px solid var(--brand-neutral-300)',
        borderRadius: 'var(--brand-radius-md)',
        padding:      '0.75rem 1rem',
        display:      'flex',
        flexDirection:'column',
        gap:          '0.5rem',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
        <Link
          to={taskDetailPath(row.id)}
          style={{ textDecoration: 'none', color: 'var(--brand-neutral-900)', flex: 1 }}
        >
          <LocationLine row={row} />
          <strong>
            {t('supervisor.room', 'Room {{number}}', { number: row.room?.room_number ?? '—' })}
          </strong>
          <div style={{ marginTop: '0.15rem', color: 'var(--brand-neutral-500)', fontSize: '0.85rem' }}>
            {t('supervisor.cleanerSubmitted', '{{cleaner}} · submitted {{when}}', {
              cleaner,
              when: completedAt,
            })}
          </div>
          <div style={{ marginTop: '0.15rem', color: 'var(--brand-neutral-500)', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
            <span>{t('supervisor.cycleType', '{{cycle}} cycle', { cycle: row.cycle_type })}</span>
            {row.is_partial_unit && (
              <span
                data-testid="partial-unit-chip"
                data-kitchen-excluded={row.kitchen_excluded ? 'true' : 'false'}
                style={{
                  display:      'inline-block',
                  padding:      '0.05rem 0.4rem',
                  borderRadius: 'var(--brand-radius-full)',
                  background:   'var(--brand-accent-subtle, var(--brand-neutral-100))',
                  color:        'var(--brand-accent, var(--brand-neutral-900))',
                  border:       '1px solid var(--brand-accent, var(--brand-neutral-300))',
                  fontSize:     '0.7rem',
                  fontWeight:   600,
                }}
              >
                Partial unit{row.kitchen_excluded ? ' · no kitchen' : ''}
              </span>
            )}
            {row.photo_coverage_status === 'unavailable' && (
              <span
                data-testid="ai-override-chip"
                style={{
                  display:      'inline-block',
                  padding:      '0.05rem 0.4rem',
                  borderRadius: 'var(--brand-radius-full)',
                  background:   'var(--brand-danger-subtle, #fee2e2)',
                  color:        'var(--brand-danger, #991b1b)',
                  border:       '1px solid var(--brand-danger, #b91c1c)',
                  fontSize:     '0.7rem',
                  fontWeight:   600,
                }}
              >
                {t('supervisor.aiOverrideChip')}
              </span>
            )}
          </div>
        </Link>
      </div>

      {actionError && <ErrorPanel message={actionError.userMessage} />}

      {showRejectForm ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          <label htmlFor={`reject-${row.id}`} style={{ fontSize: '0.85rem', color: 'var(--brand-neutral-700)' }}>
            {t('supervisor.rejectReasonLabel', 'Reason (required)')}
          </label>
          <textarea
            id={`reject-${row.id}`}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            rows={2}
            disabled={isBusy}
            placeholder={t('supervisor.rejectReasonPlaceholder', 'What does the cleaner need to fix?') ?? ''}
            style={{
              padding:      '0.5rem',
              border:       '1px solid var(--brand-neutral-300)',
              borderRadius: 'var(--brand-radius-sm)',
              fontFamily:   'inherit',
              fontSize:     '0.9rem',
              resize:       'vertical',
            }}
          />
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button
              kind="danger"
              disabled={isBusy || rejectReason.trim().length === 0}
              onClick={onReject}
            >
              {reject.isPending
                ? t('supervisor.rejecting', 'Rejecting…')
                : t('supervisor.confirmReject', 'Send back')}
            </Button>
            <Button
              kind="secondary"
              disabled={isBusy}
              onClick={() => { setShowRejectForm(false); setRejectReason(''); setActionError(null) }}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button kind="primary" disabled={isBusy} onClick={onApprove}>
            {approve.isPending
              ? t('supervisor.approving', 'Approving…')
              : t('supervisor.approve', 'Approve')}
          </Button>
          <Button
            kind="danger-outline"
            disabled={isBusy}
            onClick={() => { setShowRejectForm(true); setActionError(null) }}
          >
            {t('supervisor.reject', 'Reject')}
          </Button>
        </div>
      )}
    </li>
  )
}

// ─────────────────────────────────────────────────────────────────────

function LocationLine({ row }: { row: PendingInspectionRow }): ReactNode {
  const parts: string[] = []
  if (row.room?.campus?.name)   parts.push(row.room.campus.name)
  if (row.room?.building?.name) parts.push(row.room.building.name)
  if (row.room?.floor?.label)   parts.push(row.room.floor.label)
  if (parts.length === 0) return null
  return (
    <div
      data-testid="row-location"
      style={{
        marginBottom: '0.15rem',
        color:        'var(--brand-neutral-500)',
        fontSize:     '0.75rem',
        letterSpacing:'0.02em',
      }}
    >
      {parts.join(' · ')}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Small UI primitives — kept inline because the page is < 250 lines and
// the buttons have no other consumer yet (CLAUDE.md rule: no premature
// abstraction).
// ─────────────────────────────────────────────────────────────────────

interface ButtonProps {
  kind:     'primary' | 'secondary' | 'danger' | 'danger-outline'
  disabled: boolean
  onClick:  () => void | Promise<void>
  children: ReactNode
}

function Button({ kind, disabled, onClick, children }: ButtonProps): ReactNode {
  const palette: Record<ButtonProps['kind'], { bg: string; fg: string; border: string }> = {
    primary:        { bg: 'var(--brand-primary)',        fg: 'var(--brand-white)',   border: 'var(--brand-primary)'    },
    secondary:      { bg: 'var(--brand-neutral-100)',    fg: 'var(--brand-neutral-900)', border: 'var(--brand-neutral-300)' },
    danger:         { bg: 'var(--brand-danger)',         fg: 'var(--brand-white)',   border: 'var(--brand-danger)'     },
    'danger-outline': { bg: 'var(--brand-white)',         fg: 'var(--brand-danger)',  border: 'var(--brand-danger)'     },
  }
  const c = palette[kind]
  return (
    <button
      type="button"
      onClick={() => { void onClick() }}
      disabled={disabled}
      style={{
        background:   c.bg,
        color:        c.fg,
        border:       `1px solid ${c.border}`,
        borderRadius: 'var(--brand-radius-md)',
        padding:      '0.5rem 0.9rem',
        fontWeight:   600,
        fontSize:     '0.9rem',
        cursor:       disabled ? 'not-allowed' : 'pointer',
        opacity:      disabled ? 0.6 : 1,
      }}
    >
      {children}
    </button>
  )
}

function Hint({ children }: { children: ReactNode }): ReactNode {
  return <p style={{ color: 'var(--brand-neutral-500)' }}>{children}</p>
}

function ErrorPanel({ message }: { message: string }): ReactNode {
  return (
    <div
      role="alert"
      style={{
        background:   'var(--brand-danger-subtle)',
        color:        'var(--brand-danger)',
        border:       '1px solid var(--brand-danger)',
        borderRadius: 'var(--brand-radius-md)',
        padding:      '0.5rem 0.75rem',
        fontSize:     '0.85rem',
      }}
    >
      {message}
    </div>
  )
}
